import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
} from '@angular/core';
import { resolveColorContext, type ContextTone } from '@quipex/shared/utils';
import { QpxButton } from '../../directives/button/button';
import type { IconColor } from '../../directives/icon/icon';
import type { AnimatedIconName } from './models';

@Component({
    selector: 'qpx-animated-icon',
    templateUrl: './animated-icon.html',
    styleUrl: './animated-icon.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[style.--mat-icon-color]': 'contextColor()',
        '[class]': 'className()',
        'aria-hidden': 'true',
    },
})
export class QpxAnimatedIcon {
    //#region Dependency injection
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef, {
        host: true,
    });
    private readonly button = inject(QpxButton, {
        optional: true,
        skipSelf: true,
    });
    //#endregion

    //#region Inputs
    public readonly name = input.required<AnimatedIconName>();
    public readonly active = input(false);
    public readonly size = input<'lg' | 'md' | 'sm' | 'xs'>();
    public readonly context = input<IconColor>();
    public readonly tone = input<ContextTone>('default');
    //#endregion

    //#region State
    private readonly resolvedSize = computed(
        () => this.size() ?? this.button?.size() ?? 'md'
    );

    protected readonly contextColor = computed(() => {
        const context = this.context();
        return context
            ? `var(${resolveColorContext(context, this.tone()).decorative.variable})`
            : null;
    });

    protected readonly className = computed(() => {
        const classNames = [
            'qpx-animated-icon',
            'qpx-icon',
            this.resolvedSize(),
        ];
        const context = this.context();

        if (this.elementRef.nativeElement.hasAttribute('matButtonIcon')) {
            classNames.push('mat-icon');
        }
        if (this.active()) classNames.push('active');
        if (context) {
            classNames.push(
                resolveColorContext(context, this.tone()).decorative.className
            );
        }

        return classNames.join(' ');
    });
    //#endregion
}
